import { useContext, useEffect } from 'react';
import useCustomHook from '@/hooks/useCustomHook';
import { AudioAutoPlayerContext } from '@/shared/Players/AudioAutoPlayer/AudioAutoPlayerContext';
import isMobileJS from 'ismobilejs';
import { useGamificationContext } from '@/shared/Gamification/GamificationContext/GamificationContext';
import useActivitySEO from '../../useActivitySEO.hook';

export default function useClassicGameActivity({
  activity,
  activityConf,
  taskProgressActions,
}) {
  const { actions: gamificationActions } = useGamificationContext();
  const audioPlayerContext = useContext(AudioAutoPlayerContext);

  const { state, actions } = useCustomHook({
    defaultState: {
      screen: 'COVER',
      activityConf: {
        menu: true,
      },
      availableTypes: [],
      availableLevels: [],
      canRestart: false,
      isMobile: false,
    },
    actions: {
      setGameConf: (conf) => {
        actions.setState({
          activityConf: { ...state.activityConf, ...conf },
        });
      },
      startGame: async (params) => {
        const mode = params?.mode;
        let newState = {
          screen: 'GAME',
          pendingOnStart: true,
        };
        if (mode) {
          newState.activityConf = { ...state.activityConf, mode };
        }
        await actions.taskProgressAPI.START();
        audioPlayerContext.actions.play(
          process.env.NEXT_PUBLIC_CLOUDFRONT_STATIC_HOST +
            '/commonGameAssets/classic/sounds/intro.mp3',
          0.5
        );
        actions.setState(newState);
      },
      restartGame: () => {
        audioPlayerContext.actions.reset();
        taskProgressActions?.onActivityRestarted();

        seoActions.onStart({ reset: true });

        actions.setState({ canRestart: false });
      },
      goToMainMenu: () => {
        seoActions.reset();
        audioPlayerContext.actions.reset();
        actions.setState({
          screen: 'COVER',
          activityConf: {
            menu: true,
          },
        });
      },
      onFinishedActivity: () => {
        actions.setState({
          canRestart: true,
        });

        seoActions.onFinish();
      },
      taskProgressAPI: {
        START: async () => {
          if (activityConf && taskProgressActions) {
            const progressData = {
              activitySubType: activity.data.subType,
              miniactivityType: activityConf.miniactivityType,
              progress: 0,
            };
            await taskProgressActions.createProgress(progressData);
          }
        },
        PROGRESS: async ({ progress, correct, answers }) => {
          const miniactivityType = state.activityConf.miniactivityType;
          const gamificationData = {
            activityId: activity.id,
            miniactivityType: miniactivityType,
            level: state.activityConf.level,
            progress,
          };
          if (answers) {
            const lastAnswer = answers[answers.length - 1];
            const lastCorrectAnswer = lastAnswer.correct ? [lastAnswer.id] : [];
            gamificationData.correctAnswers = lastCorrectAnswer;
          } else if (miniactivityType == 'memoryGame') {
            gamificationData.correctAnswers = [`memory_${progress}%`];
          }
          gamificationActions.putData(gamificationData);

          if (activityConf && taskProgressActions) {
            if (activityConf.progress) {
              const progressData = {
                activitySubType: activity.data.subType,
                miniactivityType: activityConf.miniactivityType,
                progress: progress,
              };
              if (activityConf.correctness) {
                progressData.correct = correct;
                progressData.answers = answers;
              }
              await taskProgressActions.updateProgress(progressData);
            }
          }
        },
      },
    },
  });

  const { actions: seoActions } = useActivitySEO({
    activity,
    activityConf: state.activityConf,
  });

  useEffect(() => {
    if (state.pendingOnStart) {
      seoActions.onStart({});
      actions.setState({ pendingOnStart: false });
    }
  }, [state.pendingOnStart]);

  useEffect(() => {
    if (activityConf) {
      actions.setState({
        activityConf,
      });
    }
  }, [activityConf]);

  useEffect(() => {
    const isMobile = isMobileJS(window.navigator).any;
    if (isMobile) {
      actions.setState({
        isMobile: true,
      });
    }
  }, []);

  return { state, actions };
}
